import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Package, Truck, Thermometer, Boxes, Weight, Ruler, Gauge, FileText,
  Phone, Mail, Star, AlertTriangle, MapPin, Loader2, CheckCircle2, XCircle, Tag,
} from "lucide-react";
import { apiFetch, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader, Card, StatusBadge } from "@/components/common";
import { RouteMap } from "@/components/RouteMap";
import { cn } from "@/lib/utils";

const STATUS = { pending: "In attesa di assegnazione", available: "Disponibile", confirmed: "Assegnato", in_progress: "In corso", completed: "Completato", transferred: "Ceduto", cancelled: "Annullato", scaduto: "Scaduto" };
const SOSTA_PHASES = [["arrivo", "Arrivo"], ["in_attesa", "In attesa"], ["inizio_operazioni", "Inizio operazioni"], ["fine_operazioni", "Fine operazioni"], ["uscita", "Uscita"]];
const EVENT_CHOICES = [["ritardo", "Ritardo"], ["traffico", "Traffico"], ["guasto", "Guasto"], ["frigo", "Problema frigo"], ["attesa_carico", "Attesa carico"], ["attesa_scarico", "Attesa scarico"], ["documenti", "Problema documenti"], ["altro", "Altro"]];
function time(iso) {
  return iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
}
function dt(iso) {
  return iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
}
const PALLET_LABEL = { italia: "Italia 100×120", eur: "EUR/EPAL 80×120", altro: "Altro" };
function longDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}
function timeWindow(from, to, fallback) {
  if (from && to) return `${from}–${to}`;
  return from || to || fallback || "";
}
function placeAddress(place, fallback) {
  if (place) {
    if (place.address) return place.address;
    const parts = [place.comune, place.provincia, place.cap].filter(Boolean).join(", ");
    if (parts) return parts;
  }
  return fallback || "";
}

export default function TripDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposePrice, setProposePrice] = useState("");
  const [eventOpen, setEventOpen] = useState(false);
  const [eventType, setEventType] = useState("");
  const [eventNote, setEventNote] = useState("");

  const reload = async () => {
    const t = await apiFetch(`/trips/${id}`);
    setTrip(t);
  };

  useEffect(() => {
    if (!id) return;
    apiFetch(`/trips/${id}`).then(setTrip).catch(() => setError("Viaggio non trovato.")).finally(() => setLoading(false));
  }, [id]);

  const run = async (key, fn) => {
    setAction(key);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message || "Riprova.");
    } finally {
      setAction("");
    }
  };

  const accept = () =>
    run("accept", async () => {
      const r = await apiFetch(`/trips/${id}/accept`, { method: "POST", body: "{}" });
      navigate(`/chat/${r.chat_id}`, { state: { title: r.chat_title }, replace: true });
    });
  const rejectTrip = () =>
    run("reject", async () => {
      await apiFetch(`/trips/${id}/reject`, { method: "POST", body: "{}" });
      navigate(-1);
    });
  const propose = () => {
    const price = Number(proposePrice);
    if (!price || price <= 0) {
      setError("Inserisci un prezzo valido.");
      return;
    }
    run("propose", async () => {
      const offer = await apiFetch(`/trips/${id}/propose`, { method: "POST", body: JSON.stringify({ price }) });
      setTrip((old) => ({ ...old, my_offer: offer }));
      setProposeOpen(false);
      setProposePrice("");
    });
  };
  const handleOffer = (offerId, decision) => run(`${offerId}-${decision}`, async () => { await apiFetch(`/offers/${offerId}/${decision}`, { method: "POST", body: "{}" }); await reload(); });
  const advance = (status) => run(`status-${status}`, async () => { await apiFetch(`/trips/${id}/status`, { method: "POST", body: JSON.stringify({ status }) }); await reload(); });
  const setSosta = (phase) => run(`sosta-${phase}`, async () => { await apiFetch(`/trips/${id}/sosta/${phase}`, { method: "POST", body: "{}" }); await reload(); });
  const reportEvent = () => {
    if (!eventType) {
      setError("Scegli il tipo di segnalazione.");
      return;
    }
    run("event", async () => {
      const ev = await apiFetch(`/trips/${id}/events`, { method: "POST", body: JSON.stringify({ type: eventType, note: eventNote.trim() }) });
      setTrip((old) => ({ ...old, events: [ev, ...(old.events ?? [])] }));
      setEventOpen(false);
      setEventType("");
      setEventNote("");
    });
  };
  const submitReview = () => {
    if (rating === 0) {
      setError("Seleziona un voto da 1 a 5 stelle.");
      return;
    }
    run("review", async () => {
      await apiFetch(`/trips/${id}/review`, { method: "POST", body: JSON.stringify({ rating, comment: reviewComment }) });
      setTrip((old) => ({ ...old, can_review: false, my_review: true }));
    });
  };
  const openChat = () =>
    run("chat", async () => {
      const other = user?.id === trip.owner_id ? trip.assigned_to : trip.owner_id;
      if (!other) return;
      const chat = await apiFetch("/chats/start", { method: "POST", body: JSON.stringify({ user_id: other, trip_id: trip.id }) });
      navigate(`/chat/${chat.id}`, { state: { title: chat.title } });
    });

  if (loading) return <Loader />;
  if (!trip) return <div className="py-16 text-center text-muted-foreground">{error || "Viaggio non trovato."}</div>;

  const isOwner = user?.id === trip.owner_id;
  const isCarrier = user?.account_type === "vettore";
  const isAssigned = trip.assigned_to === user?.id;
  const canAct = isCarrier && !isOwner && (trip.status === "pending" || trip.status === "available");
  const contacts = trip.contacts || {};

  const detail = (Icon, label, value) =>
    value ? (
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-[13px] text-muted-foreground">{label}:</span>
        <span className="text-[13px] font-bold text-card-foreground">{value}</span>
      </div>
    ) : null;

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-foreground">
        <ArrowLeft className="h-5 w-5" /> <span className="font-extrabold">Dettaglio viaggio</span>
      </button>

      <Card className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-card-foreground">{trip.date}{trip.time ? ` · ${trip.time}` : ""} · {trip.vehicle_type}</p>
          {trip.price_proposed != null ? <span className="shrink-0 text-xl font-extrabold text-primary">€ {trip.price_proposed}</span> : null}
        </div>
        <StatusBadge tone={trip.status === "completed" ? "success" : "neutral"}>{STATUS[trip.status] || trip.status}</StatusBadge>
        {trip.parent_trip_id ? (
          <p className="rounded-lg bg-secondary px-3 py-2 text-xs font-bold text-secondary-foreground">
            Cessione tra vettori · il prezzo mostrato è quello proposto dal vettore cedente.
          </p>
        ) : null}
      </Card>

      {trip.owner_id === user?.id && ["confirmed", "in_progress"].includes(trip.status) ? (
        <Card className="space-y-2">
          <p className="text-sm font-extrabold text-foreground">Gestione viaggio</p>
          <p className="text-xs text-muted-foreground">Il viaggio è assegnato: non è più eliminabile definitivamente. Puoi annullarlo avvisando il vettore.</p>
          <button
            data-testid="trip-cancel-button"
            onClick={() => { if (window.confirm("Annullare questo viaggio? Il vettore verrà avvisato.")) advance("cancelled"); }}
            disabled={action === "status-cancelled"}
            className="flex items-center gap-1.5 rounded-full border border-destructive px-4 py-2 text-xs font-extrabold text-destructive hover:bg-destructive/10 disabled:opacity-60"
          >
            {action === "status-cancelled" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Annulla viaggio
          </button>
        </Card>
      ) : null}

      <Card className="space-y-1.5">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-primary" />
          <p className="text-sm font-extrabold text-foreground">Carico</p>
        </div>
        <p className="whitespace-pre-wrap break-words text-[15px] font-extrabold text-card-foreground">{trip.pickup_place?.name || trip.pickup}</p>
        <p className="whitespace-pre-wrap break-words text-[13px] text-muted-foreground">{placeAddress(trip.pickup_place, trip.pickup)}</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-[13px]">
          <span className="text-muted-foreground">Data: <span className="font-bold text-card-foreground">{longDate(trip.date) || "—"}</span></span>
          {timeWindow(trip.load_from, trip.load_to, trip.time) ? (
            <span className="text-muted-foreground">Fascia oraria prevista: <span className="font-bold text-card-foreground">{timeWindow(trip.load_from, trip.load_to, trip.time)}</span></span>
          ) : null}
        </div>
        {trip.load_notes ? <p className="whitespace-pre-wrap break-words text-[12px] text-muted-foreground">Note carico: {trip.load_notes}</p> : null}
      </Card>

      <Card className="space-y-1.5">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-primary" />
          <p className="text-sm font-extrabold text-foreground">Scarico</p>
        </div>
        <p className="whitespace-pre-wrap break-words text-[15px] font-extrabold text-card-foreground">{trip.delivery_place?.name || trip.delivery}</p>
        <p className="whitespace-pre-wrap break-words text-[13px] text-muted-foreground">{placeAddress(trip.delivery_place, trip.delivery)}</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-[13px]">
          <span className="text-muted-foreground">Data: <span className="font-bold text-card-foreground">{longDate(trip.unload_date || trip.date) || "—"}</span></span>
          {timeWindow(trip.unload_from, trip.unload_to, "") ? (
            <span className="text-muted-foreground">Fascia oraria prevista: <span className="font-bold text-card-foreground">{timeWindow(trip.unload_from, trip.unload_to, "")}</span></span>
          ) : null}
        </div>
        {trip.unload_notes ? <p className="whitespace-pre-wrap break-words text-[12px] text-muted-foreground">Note scarico: {trip.unload_notes}</p> : null}
      </Card>

      <Card className="space-y-2.5">
        <p className="text-sm font-extrabold text-foreground">Dettagli carico</p>
        {detail(Package, "Merce", trip.cargo || "Trasporto merci")}
        {detail(Boxes, "Formato pallet", PALLET_LABEL[trip.pallet_format] || trip.pallet_format)}
        {detail(Boxes, "Pallet", trip.pallets)}
        {detail(Weight, "Peso", trip.weight)}
        {detail(Thermometer, "Temperatura", trip.temperature)}
        {detail(Truck, "Mezzo", trip.vehicle_type)}
        {detail(Ruler, "Lunghezza", trip.length)}
        {detail(Gauge, "Portata", trip.payload_capacity)}
        {detail(Truck, "Classe Euro", trip.euro_class)}
        {trip.requirements?.length ? detail(CheckCircle2, "Requisiti", trip.requirements.join(" · ")) : null}
        {trip.notes ? <p className="border-t border-border pt-2 text-[13px] text-muted-foreground">{trip.notes}</p> : null}
        <p className="text-xs text-muted-foreground">Pubblicato da: <span className="font-bold text-card-foreground">{trip.owner_name}</span></p>
      </Card>

      {trip.attachment_files?.length ? (
        <Card className="space-y-2">
          <p className="text-sm font-extrabold text-foreground">Allegati</p>
          {trip.attachment_files.map((a) => (
            <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[13px] font-bold text-primary">
              <FileText className="h-4 w-4" /> {a.name}
            </a>
          ))}
        </Card>
      ) : null}

      {contacts.owner || contacts.carrier ? (
        <Card className="space-y-2">
          <p className="text-sm font-extrabold text-foreground">Contatti</p>
          {[contacts.owner, contacts.carrier].filter(Boolean).map((c, i) => (
            <div key={i} className="space-y-1">
              <p className="text-[13px] font-bold text-card-foreground">{c.company_name}</p>
              {c.phone ? <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-[13px] text-primary"><Phone className="h-4 w-4" /> {c.phone}</a> : null}
              {c.email ? <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-[13px] text-primary"><Mail className="h-4 w-4" /> {c.email}</a> : null}
            </div>
          ))}
          {(isOwner && trip.assigned_to) || isAssigned ? (
            <button onClick={openChat} disabled={action === "chat"} className="mt-1 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-primary text-sm font-extrabold text-primary">
              {action === "chat" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Apri chat
            </button>
          ) : null}
        </Card>
      ) : null}

      {/* Carrier actions on open trip */}
      {canAct ? (
        <Card className="space-y-2.5">
          <p className="text-sm font-extrabold text-foreground">Azioni</p>
          {trip.my_offer ? (
            <p className="rounded-lg bg-secondary px-3 py-2 text-[13px] font-bold text-secondary-foreground">
              Hai proposto € {trip.my_offer.price} · {trip.my_offer.status === "pending" ? "in attesa di risposta" : trip.my_offer.status}
            </p>
          ) : null}
          <button data-testid="trip-accept-button" onClick={accept} disabled={action === "accept"} className="flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
            {action === "accept" ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Accetta viaggio
          </button>
          <div className="flex gap-2">
            <button data-testid="trip-propose-button" onClick={() => { setProposeOpen((v) => !v); setError(""); }} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-primary py-2.5 text-sm font-extrabold text-primary">
              <Tag className="h-4 w-4" /> Proponi prezzo
            </button>
            <button data-testid="trip-reject-button" onClick={rejectTrip} disabled={action === "reject"} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-destructive py-2.5 text-sm font-extrabold text-destructive">
              <XCircle className="h-4 w-4" /> Rifiuta
            </button>
          </div>
          {proposeOpen ? (
            <div className="space-y-2 border-t border-border pt-2.5">
              <input data-testid="propose-price-input" value={proposePrice} onChange={(e) => setProposePrice(e.target.value)} placeholder="Prezzo proposto (€)" inputMode="numeric" className="min-h-[48px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary" />
              <button data-testid="propose-submit-button" onClick={propose} disabled={action === "propose"} className="flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-[13px] font-extrabold text-primary-foreground disabled:opacity-60">
                {action === "propose" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} INVIA PROPOSTA
              </button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {/* Owner: incoming offers */}
      {isOwner && trip.offers?.length ? (
        <Card className="space-y-2.5">
          <p className="text-sm font-extrabold text-foreground">Proposte ricevute</p>
          {trip.offers.filter((o) => o.status === "pending").map((o) => (
            <div key={o.id} data-testid={`trip-offer-${o.id}`} className="flex items-center gap-2 border-t border-border pt-2.5 first:border-0 first:pt-0">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-bold text-card-foreground">{o.carrier_name}</p>
                <p className="text-xs text-muted-foreground">€ {o.price}</p>
              </div>
              <button data-testid={`offer-accept-${o.id}`} onClick={() => handleOffer(o.id, "accept")} disabled={action === `${o.id}-accept`} className="rounded-lg bg-primary px-3 py-2 text-xs font-extrabold text-primary-foreground">Accetta</button>
              <button data-testid={`offer-reject-${o.id}`} onClick={() => handleOffer(o.id, "reject")} disabled={action === `${o.id}-reject`} className="rounded-lg border border-destructive px-3 py-2 text-xs font-extrabold text-destructive">Rifiuta</button>
            </div>
          ))}
          {trip.offers.filter((o) => o.status === "pending").length === 0 ? <p className="text-[13px] text-muted-foreground">Nessuna proposta in attesa.</p> : null}
        </Card>
      ) : null}

      {/* Operational: status advance + sosta + events (assigned carrier/driver) */}
      {trip.can_operate ? (
        <Card className="space-y-3">
          <p className="text-sm font-extrabold text-foreground">Operatività</p>
          <div className="flex flex-wrap gap-2">
            {trip.status === "confirmed" ? <button onClick={() => advance("in_progress")} disabled={action === "status-in_progress"} className="rounded-full bg-primary px-4 py-2 text-xs font-extrabold text-primary-foreground">Avvia trasporto</button> : null}
            {trip.status === "in_progress" ? <button onClick={() => advance("completed")} disabled={action === "status-completed"} className="rounded-full bg-[hsl(var(--success))] px-4 py-2 text-xs font-extrabold text-white">Segna consegnato</button> : null}
          </div>
          <div>
            <p className="mb-1.5 text-xs font-bold text-card-foreground">Registra sosta al deposito</p>
            <div className="flex flex-wrap gap-2">
              {SOSTA_PHASES.map(([k, l]) => (
                <button key={k} data-testid={`sosta-${k}`} onClick={() => setSosta(k)} disabled={action === `sosta-${k}`} className={cn("rounded-full px-3 py-2 text-xs font-bold", trip.sosta?.[k] ? "bg-[hsl(var(--success))] text-white" : "bg-secondary text-secondary-foreground")}>
                  {l}{trip.sosta?.[k] ? ` · ${time(trip.sosta[k])}` : ""}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="event-open-button" onClick={() => setEventOpen((v) => !v)} className="flex items-center gap-2 text-[13px] font-extrabold text-primary">
            <AlertTriangle className="h-4 w-4" /> Segnala un imprevisto
          </button>
          {eventOpen ? (
            <div className="space-y-2 border-t border-border pt-2.5">
              <div className="flex flex-wrap gap-2">
                {EVENT_CHOICES.map(([k, l]) => (
                  <button key={k} data-testid={`event-type-${k}`} onClick={() => setEventType(k)} className={cn("rounded-full px-3 py-1.5 text-xs font-bold", eventType === k ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground")}>{l}</button>
                ))}
              </div>
              <input value={eventNote} onChange={(e) => setEventNote(e.target.value)} placeholder="Nota (facoltativa)" className="min-h-[46px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-primary" />
              <button data-testid="event-submit-button" onClick={reportEvent} disabled={action === "event"} className="flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-[13px] font-extrabold text-primary-foreground disabled:opacity-60">
                {action === "event" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} INVIA SEGNALAZIONE
              </button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {/* Events log */}
      {trip.events?.length ? (
        <Card className="space-y-2">
          <p className="text-sm font-extrabold text-foreground">Segnalazioni</p>
          {trip.events.map((ev) => (
            <div key={ev.id} className="border-t border-border pt-2 first:border-0 first:pt-0">
              <p className="text-[13px] font-bold text-card-foreground">{ev.label}</p>
              {ev.note ? <p className="text-xs text-muted-foreground">{ev.note}</p> : null}
              <p className="text-[11px] text-muted-foreground">{ev.created_by_name} · {dt(ev.created_at)}</p>
            </div>
          ))}
        </Card>
      ) : null}

      {/* Review */}
      {trip.can_review ? (
        <Card className="space-y-2.5">
          <p className="text-sm font-extrabold text-foreground">Lascia una recensione</p>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} data-testid={`review-star-${s}`} onClick={() => setRating(s)}>
                <Star className={cn("h-7 w-7", s <= rating ? "fill-[hsl(var(--warning))] text-[hsl(var(--warning))]" : "text-[hsl(var(--warning))]")} />
              </button>
            ))}
          </div>
          <input value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Commento (facoltativo)" className="min-h-[46px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-primary" />
          <button data-testid="review-submit-button" onClick={submitReview} disabled={action === "review"} className="flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-[13px] font-extrabold text-primary-foreground disabled:opacity-60">
            {action === "review" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} INVIA RECENSIONE
          </button>
        </Card>
      ) : trip.my_review ? (
        <p className="text-center text-sm text-muted-foreground">Hai già recensito questo viaggio. Grazie!</p>
      ) : null}

      <RouteMap
        pickup={trip.pickup}
        delivery={trip.delivery}
        pickupCoord={trip.pickup_place?.lat != null ? [trip.pickup_place.lat, trip.pickup_place.lon] : trip.pickup_coords}
        deliveryCoord={trip.delivery_place?.lat != null ? [trip.delivery_place.lat, trip.delivery_place.lon] : trip.delivery_coords}
      />

      {error ? <p data-testid="trip-error" className="text-center text-sm font-semibold text-destructive">{error}</p> : null}
    </div>
  );
}
